// src/providers/NotificationProvider.jsx
import React, { createContext, useContext, useMemo } from "react";
import { notification, ConfigProvider, theme } from "antd";

// Tipado simple
const NotifyContext = createContext(null);

export function NotificationProvider({ children }) {
    const [api, contextHolder] = notification.useNotification();

    const notify = useMemo(() => ({
        success: (msg, opts = {}) => api.success({ message: "Éxito", description: msg, placement: "topRight", duration: 3, ...opts, }),
        info: (msg, opts = {}) => api.info({ message: "Información", description: msg, placement: "topRight", duration: 3, ...opts }),
        warning: (msg, opts = {}) => api.warning({ message: "Atención", description: msg, placement: "topRight", duration: 4, ...opts }),
        error: (msg, opts = {}) => api.error({ message: "Error", description: msg, placement: "topRight", duration: 5, ...opts }),
        open: (args) => api.open({ placement: "topRight", duration: 3, ...args }),
        // También se puede exponer api directamente si lo necesitas
        api,
    }), [api]);

    return (
        <NotifyContext.Provider value={notify}>
            {/* Opcional: envolver con ConfigProvider para temas y locale */}
            <ConfigProvider
                theme={{
                    algorithm: theme.defaultAlgorithm,
                    token: {
                        colorInfo: "#1677ff",
                    },
                }}
            >
                {contextHolder}
                {children}
            </ConfigProvider>
        </NotifyContext.Provider>
    );
}

// Helper para envolver promesas con notificaciones de loading/success/error
export async function withNotify(promise, { loading = "Procesando...", success = "Operación completada", error = "Ocurrió un error", key } = {}) {
    // Obtener notify desde un singleton de notification (sin React hook)
    // Mejor: usar el contexto
    // Para usar el contexto dentro de helpers, exportar una función que lo tome por parámetro:
    throw new Error("withNotify(promise, opts) requiere la versión ligada al contexto: useWithNotify()");
}

// Versión ligada al contexto (recomendada)
export function useWithNotify() {
    const notify = useNotify();
    return async (promise, { loading = "Procesando...", success = "Operación completada", error = "Ocurrió un error", key } = {}) => {
        const k = key || `noti_${Date.now()}`;
        notify.open({ key: k, message: "Estado", description: loading, duration: 0 });
        try {
            const res = await promise;
            notify.success(success, { key: k, duration: 2 });
            return res;
        } catch (e) {
          
            console.error("Error en withNotify:", e);
            notify.error(
                (() => {
                    console.error('Detalle del error:', e);
                    // Extraer mensaje
                    const msg =
                    
                    e.data?.message ||
                        e.error ||
                        e.message ||
                        e.data?.error ||
                        e.response?.data?.error ||
                        e.response?.data?.message ||
                        e.details ||
                        'Ha ocurrido un error';

                    // Detectar tipo de error
                    const msgLower = String(msg).toLowerCase();

                    if (msgLower.includes('already exists') ||
                        msgLower.includes('ya existe') ||
                        msgLower.includes('duplicado')) {
                        return 'Este registro ya existe en el sistema';
                    }

                    if (msgLower.includes('solapa') || msgLower.includes('overlap')) {
                        return msg; // Mantener mensaje detallado para solapamientos
                    }

                    return msg;
                })(),
                { key: k, duration: 5 }
            );
            throw e;
        }
    };
}

export function useNotify() {
    const ctx = useContext(NotifyContext);
    if (!ctx) {
        throw new Error("useNotify debe usarse dentro de NotificationProvider");
    }
    return ctx;
}
